import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import { initWebVitals } from './lib/webVitals'
import './lib/i18n'; // Initialize i18n

// Initialize Web Vitals monitoring for real user performance tracking
initWebVitals();

// Global error handler for dynamic import failures (after deployments)
/**
 * Last-resort UI when the chunk auto-reload guard gives up. Without this the user
 * is left staring at a blank screen (the app never mounted). Deliberately plain
 * DOM + inline styles: React/i18n may not have loaded, which is the whole reason
 * we are here. Copy is picked from <html lang> so Arabic users get Arabic.
 */
function showChunkErrorNotice() {
  if (document.getElementById('chunk-error-notice')) return;
  const isAr = (document.documentElement.lang || '').startsWith('ar');
  const dir = isAr ? 'rtl' : 'ltr';
  const title = isAr ? 'تعذّر تحميل التطبيق' : "Couldn't load the app";
  const body = isAr
    ? 'يبدو أن هناك تحديثًا جديدًا أو مشكلة في الاتصال. حاول إعادة التحميل.'
    : 'There may be a new version available, or your connection dropped. Try reloading.';
  const retry = isAr ? 'إعادة المحاولة' : 'Reload';

  const el = document.createElement('div');
  el.id = 'chunk-error-notice';
  el.setAttribute('dir', dir);
  el.setAttribute('role', 'alert');
  el.style.cssText = [
    'position:fixed', 'inset:0', 'z-index:2147483647', 'display:flex',
    'flex-direction:column', 'align-items:center', 'justify-content:center',
    'gap:12px', 'padding:24px', 'text-align:center', 'background:#fff',
    'color:#111', 'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
  ].join(';');
  el.innerHTML =
    '<div style="font-size:18px;font-weight:700">' + title + '</div>' +
    '<div style="font-size:14px;max-width:32rem;line-height:1.5;color:#444">' + body + '</div>';

  const btn = document.createElement('button');
  btn.textContent = retry;
  btn.style.cssText = [
    'min-height:44px', 'padding:0 20px', 'border-radius:8px', 'border:0',
    'background:#111', 'color:#fff', 'font-size:16px', 'font-weight:600', 'cursor:pointer',
  ].join(';');
  btn.onclick = () => {
    try { sessionStorage.removeItem('chunk_reload_timestamps'); } catch { /* private mode */ }
    window.location.reload();
  };
  el.appendChild(btn);
  document.body.appendChild(el);
}

// This handles the case where cached JS tries to load chunks that no longer exist
window.addEventListener('error', (event) => {
  // Check if it's a module loading error (happens after deployment when chunks change)
  if (
    event.message?.includes('Failed to fetch dynamically imported module') ||
    event.message?.includes('Loading chunk') ||
    event.message?.includes('Loading CSS chunk')
  ) {
    console.warn('Detected chunk loading error - refreshing to get latest version');
    // Clear caches and reload
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }
    // Reload, but avoid infinite loops (max 3 reloads within 10 seconds)
    const lastReloadStr = sessionStorage.getItem('chunk_reload_timestamps');
    const now = Date.now();
    let reloads: number[] = lastReloadStr ? JSON.parse(lastReloadStr) : [];

    // Filter out old timestamps
    reloads = reloads.filter(time => now - time < 10000);

    if (reloads.length < 3) {
      reloads.push(now);
      sessionStorage.setItem('chunk_reload_timestamps', JSON.stringify(reloads));
      // Refresh the worker FIRST. A bare reload is served by the SAME stale
      // service worker and fails identically, which is why this guard burned all
      // three attempts and still landed on the error notice — the loop that
      // needed a manual cache clear to escape, three times in one week.
      reloadThroughFreshWorker();
    } else {
      console.error('Too many chunk load errors, stopping auto-reload to prevent loop.');
      showChunkErrorNotice();
    }
  }
});

// Also handle unhandled promise rejections for dynamic imports
window.addEventListener('unhandledrejection', (event) => {
  if (
    event.reason?.message?.includes('Failed to fetch dynamically imported module') ||
    event.reason?.message?.includes('Loading chunk') ||
    event.reason?.name === 'ChunkLoadError'
  ) {
    console.warn('Detected chunk loading rejection - refreshing to get latest version');

    // Reload, but avoid infinite loops
    const lastReloadStr = sessionStorage.getItem('chunk_reload_timestamps');
    const now = Date.now();
    let reloads: number[] = lastReloadStr ? JSON.parse(lastReloadStr) : [];

    // Filter out old timestamps
    reloads = reloads.filter(time => now - time < 10000);

    if (reloads.length < 3) {
      reloads.push(now);
      sessionStorage.setItem('chunk_reload_timestamps', JSON.stringify(reloads));
      // Refresh the worker FIRST. A bare reload is served by the SAME stale
      // service worker and fails identically, which is why this guard burned all
      // three attempts and still landed on the error notice — the loop that
      // needed a manual cache clear to escape, three times in one week.
      reloadThroughFreshWorker();
    } else {
      // Previously fell through silently, leaving a blank screen — this is the
      // path a failed lazy-route import actually takes.
      console.error('Too many chunk load rejections, stopping auto-reload.');
      showChunkErrorNotice();
    }
  }
});

/** Reload, but make sure the service worker fetches new content first. */
function reloadThroughFreshWorker() {
  if (!('serviceWorker' in navigator)) { window.location.reload(); return; }
  navigator.serviceWorker.getRegistration()
    .then((r) => r?.update())
    .catch(() => undefined)
    .finally(() => window.location.reload());
}

/**
 * Keep the service worker honest.
 *
 * `registerType: 'autoUpdate'` only updates when something asks it to, and
 * nothing did — so a member could sit on a shell cached days earlier and never
 * be offered the new one. That is how Ahmed's leaderboard rendered blank until
 * the cache was cleared by hand, and it corrupts any verification we ask him to
 * do: "it still looks broken" stops being evidence.
 *
 * Ask for an update on load, hourly, and whenever the tab regains focus; reload
 * exactly once when a new worker takes control.
 */
function keepServiceWorkerFresh() {
  if (!('serviceWorker' in navigator)) return;

  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });

  navigator.serviceWorker.ready.then((registration) => {
    const check = () => { registration.update().catch(() => { /* offline; try later */ }); };
    check();
    setInterval(check, 60 * 60 * 1000);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') check();
    });
  }).catch(() => { /* nothing controlling this page */ });
}

keepServiceWorkerFresh();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)

